// Vendor
import { warn } from "vue";
import type { Locale } from "vue-i18n";

/**
 * useLocalizedHotelPath is a composable function that generates a localized path for a hote info page based on a property slug or params object.
 *
 *
 * @returns A function that returns a localized path for a hotel based on a property slug or params object.
 * @example
 * ```ts
 * const localizedHotelPath = useLocalizedHotelPath();
 * const path = localizedHotelPath("europa/paris/campos-eliseos-hotel", "es");
 * // path will be something like "/es/hoteles/europa/paris/campos-eliseos-hotel"
 * ```
 */
export default function useLocalizedHotelPath() {
  const localePath = useLocalePath();

  /**
   * @param route - The route string or an object containing the continent, city, and hotelSlug.
   * @param locale - The locale to use for the path generation.
   */
  return (route: string | LocalizedHotelParams, locale?: Locale) => {
    let continent: string | undefined;
    let city: string | undefined;
    let hotelSlug: string | undefined;

    if (typeof route === "string") {
      [continent, city, hotelSlug] = route.split("/");
    } else {
      ({ continent, city, hotelSlug } = route || {});
    }

    if (!continent) {
      warn(
        `useLocalizedHotelPath: continent could not be resolved from route "${route}"`,
      );

      return null;
    }

    if (!city) {
      warn(
        `useLocalizedHotelPath: city could not be resolved from route "${route}"`,
      );

      return null;
    }

    if (!hotelSlug) {
      warn(
        `useLocalizedHotelPath: hotel slug could not be resolved from route "${route}"`,
      );

      return null;
    }

    return localePath(
      {
        name: "hotels-continent-city-hotelSlug",
        params: {
          continent,
          city,
          hotelSlug,
        },
      },
      locale,
    );
  };
}
